/* eslint-disable functional/no-expression-statement */
import * as D3Axis from "d3-axis";
import * as D3Scale from "d3-scale";
import moment from "moment-timezone";

import * as Color from "~/Color";
import { ChartGeneratorProps, LogChartData, Status, SVG } from "~/Log/Chart";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import * as ChartIcon from "./Icon";

const sumDurationOfBand = (
  bandToSum: Status,
  data: readonly LogChartData[]
): string => {
  const duration = moment.duration(
    data
      .filter(({ status }) => status === bandToSum)
      .reduce(
        (previous, { start, stop }) =>
          previous + (stop.valueOf() - start.valueOf()),
        0
      )
  );
  return Time.Duration.to24HourHHMMSS({ duration: duration.asMilliseconds() });
};

export namespace Yaxis {
  export namespace Left {
    export const generate = ({
      svg,
      yScale,
      chartWidth,
    }: ChartGeneratorProps): void => {
      svg
        .append("g")
        .attr("class", "axis axis-y-left")
        .call(
          D3Axis.axisLeft(yScale as D3Axis.AxisScale<string>)
            .ticks(4)
            .tickSize(-chartWidth)
            .tickPadding(15)
        )
        .call((g) => g.select(".domain").remove());

      svg
        .selectAll(".axis-y-left .tick line")
        .style("stroke-dasharray", "4 1")
        .style("stroke", Color.veryWhiteGrey());

      svg
        .selectAll(".axis-y-left .tick text")
        .style("font-size", Typography.FontSize.D3.data())
        .style("font-weight", "bold");
    };

    export namespace Icons {
      export const generate = ({
        svg,
        yScale,
        topMargin,
      }: ChartGeneratorProps): void => {
        const leftAxisIconGroup = svg
          .append("g")
          .attr("class", "y-icon")
          .attr("transform", `translate(-56,${topMargin - 2})`);

        leftAxisIconGroup
          .append("svg")
          .attr("class", `y-icon-off ${ChartIcon.toggleOffSVGData.class}`)
          .attr("viewBox", ChartIcon.toggleOffSVGData.viewBox)
          .attr("width", ChartIcon.toggleOffSVGData.width)
          .attr("height", ChartIcon.toggleOffSVGData.height)
          .attr("x", -(ChartIcon.toggleOffSVGData.width / 2))
          .attr(
            "y",
            (yScale("OFF") ?? 0) - ChartIcon.toggleOffSVGData.height / 2
          )
          .append("path")
          .attr("fill", ChartIcon.toggleOffSVGData.fill)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          .attr("d", ChartIcon.toggleOffSVGData.path);

        leftAxisIconGroup
          .append("svg")
          .attr("class", `y-icon-sb ${ChartIcon.sbSVGData.class}`)
          .attr("viewBox", ChartIcon.sbSVGData.viewBox)
          .attr("width", ChartIcon.sbSVGData.width)
          .attr("height", ChartIcon.sbSVGData.height)
          .attr("x", -(ChartIcon.sbSVGData.width / 2))

          .attr("y", (yScale("SB") ?? 0) - 3 - ChartIcon.sbSVGData.height / 2)
          .append("path")
          .attr("fill", ChartIcon.sbSVGData.fill)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          .attr("d", ChartIcon.sbSVGData.path);

        leftAxisIconGroup
          .append("svg")
          .attr("class", `y-icon-d ${ChartIcon.truckSVGData.class}`)
          .attr("viewBox", ChartIcon.truckSVGData.viewBox)
          .attr("width", ChartIcon.truckSVGData.width)
          .attr("height", ChartIcon.truckSVGData.height)
          .attr("x", -(ChartIcon.truckSVGData.width / 2))
          .attr("y", (yScale("D") ?? 0) - 3 - ChartIcon.truckSVGData.height / 2)
          .append("path")
          .attr("fill", ChartIcon.truckSVGData.fill)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          .attr("d", ChartIcon.truckSVGData.path);

        leftAxisIconGroup
          .append("svg")
          .attr("class", `y-icon-on ${ChartIcon.toggleOnSVGData.class}`)
          .attr("viewBox", ChartIcon.toggleOnSVGData.viewBox)
          .attr("width", ChartIcon.toggleOnSVGData.width)
          .attr("height", ChartIcon.toggleOnSVGData.width)
          .attr("x", -(ChartIcon.toggleOnSVGData.width / 2))
          .attr(
            "y",
            (yScale("ON") ?? 0) - 3 - ChartIcon.toggleOnSVGData.height / 2
          )
          .append("path")
          .attr("fill", ChartIcon.toggleOnSVGData.fill)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          .attr("d", ChartIcon.toggleOnSVGData.path);
      };
    }
  }

  export namespace Right {
    const indexToStatusColor = (index: number): Color.Color =>
      index === 0 ? Color.green() : index === 1 ? Color.azure() : Color.steel();

    export const generate = ({
      svg,
      statusEvents,
      chartWidth,
      yScaleRight,
      dayStart,
      driverTimezone,
    }: ChartGeneratorProps): void => {
      const interval = pipe(
        Time.DateTime.toDayInterval(dayStart, driverTimezone),
        Time.Interval.Stopped.expandRight(Time.Duration.seconds(1))
      );

      const statusEventsForDay = statusEvents
        .filter((_d, index) => index % 2 === 0)
        .filter((d) =>
          pipe(
            Time.Interval.Stopped.fromDateTimes(
              {
                dateTime: moment.utc(d.start).tz(driverTimezone.zone).valueOf(),
              },
              { dateTime: moment.utc(d.stop).tz(driverTimezone.zone).valueOf() }
            ),
            Time.Interval.Stopped.contained(interval)
          )
        );

      svg
        .append("g")
        .attr("class", "axis axis-y-right")
        .attr("transform", `translate( ${chartWidth}, 0 )`)
        .call(
          D3Axis.axisRight(yScaleRight as D3Axis.AxisScale<string>)
            .ticks(4)
            .tickSize(0)
            .tickPadding(15)
            .tickFormat((d) =>
              sumDurationOfBand(d as Status, statusEventsForDay)
            )
        )
        .call((g) => g.select(".domain").remove());

      svg
        .selectAll(".axis-y-right .tick text")
        .style("fill", (_d, index) => indexToStatusColor(index))
        .style("font-size", Typography.FontSize.D3.data());
    };
  }
}

export namespace Xaxis {
  const xAxis = (
    xScale: D3Scale.ScaleTime<number, number, never>,
    width: number
  ) =>
    D3Axis.axisBottom(xScale).ticks(width > 1600 ? 25 : width > 1400 ? 15 : 10);

  const formatAxis = (svg: SVG) => {
    svg
      .selectAll(".axis.axis-x .tick text")
      .attr("fill", Color.steel())
      .attr("font-size", Typography.FontSize.D3.data());

    svg
      .selectAll(".axis.axis-x .tick line")
      .style("stroke", Color.veryWhiteGrey());

    svg
      .selectAll(".axis.axis-x .domain")
      .style("stroke", Color.veryWhiteGrey());
  };

  export const generate = ({
    svg,
    chartHeight,
    chartWidth,
    xScale,
  }: ChartGeneratorProps) => {
    svg
      .append("g")
      .attr("class", "axis axis-x")
      .attr("transform", `translate(0,${chartHeight})`)
      .call(xAxis(xScale, chartWidth));

    formatAxis(svg);
  };

  export const update = ({ svg, xScale, chartWidth }: ChartGeneratorProps) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    svg.select(".axis.axis-x").call(xAxis(xScale, chartWidth));
    formatAxis(svg);
  };
}
